import React, { useMemo, useEffect, useState, useCallback } from "react";
import "./styles.css";
import { ReactTable } from "../../Components/Molecules";
import PATHS from "../../Routes/paths";
import { DEALER_TABLE_HEADERS, OEM_TABLE_HEADERS } from "../../Library/tableHeaders";
import { Button, Spinner } from "@chakra-ui/react";
import { AddNew } from "../../Assets/images/svgs";
import { useLocation, useNavigate } from "react-router-dom";
import { getSellerDealers, getAllMakes } from "../../APIs";
import { Breadcrumb, Switcher } from "../../Components/Atoms";

const ShowDealers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [allDealers, setAllDealers] = useState([]);
  const [filteredDealers, setFilteredDealers] = useState([]);
  const [allOems, setAllOems] = useState([]);
  const [showLoader, setShowLoader] = useState(true);
  const [selected, setSelected] = useState(1);
  const [oemId, setOemId] = useState(null);
  const [oemDetails, setOemDetails] = useState(null);

  const fetchDealersAndMakes = useCallback(() => {
    getSellerDealers((err, dealersData) => {
      if (err) {
        console.error("Error fetching Dealers", err);
      } else {
        getAllMakes((err, makesData) => {
          if (err) {
            console.error("Error fetching Makes", err);
          } else {
            const makesMap = makesData.reduce((acc, make) => {
              acc[make.idx] = make;
              return acc;
            }, {});

            const processedDealers = dealersData.map((dealer) => {
              dealer.totalDevices = (dealer.stock || 0) + (dealer.installed || 0);
              dealer.oem = makesMap[dealer.mk]?.nm || "Unknown OEM";
              return dealer;
            });

            setAllDealers(processedDealers);

            const groupedOems = processedDealers.reduce((acc, dealer) => {
              const oemId = dealer.mk;
              if (!acc[oemId]) {
                acc[oemId] = {
                  idx: oemId,
                  oemNm: makesMap[oemId]?.nm || "Unknown OEM",
                  nod: 0,
                  stock: 0,
                  installed: 0,
                  totalDevices: 0,
                };
              }
              acc[oemId].nod += 1;
              acc[oemId].stock += dealer.stock || 0;
              acc[oemId].installed += dealer.installed || 0;
              acc[oemId].totalDevices += dealer.totalDevices;
              return acc;
            }, {});

            setAllOems(Object.values(groupedOems));
            setShowLoader(false);
          }
        });
      }
    });
  }, []);

  useEffect(() => {
    fetchDealersAndMakes();
  }, [fetchDealersAndMakes]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const oemIdParam = Number(searchParams.get("oemId"));

    if (oemIdParam) {
      setOemId(oemIdParam);
      setSelected(1);

      const filtered = allDealers.filter((dealer) => dealer.mk === oemIdParam);
      setFilteredDealers(filtered);

      const oem = allOems.find((oem) => oem.idx === oemIdParam);
      setOemDetails(oem);
    } else {
      setOemId(null);
      setFilteredDealers(allDealers);
    }
  }, [location.search, allDealers, allOems]);

  const dealerTableColumns = useMemo(
    () =>
      Object.values(DEALER_TABLE_HEADERS).map((header) => ({
        Header: header.nm,
        accessor: header.key,
        disableSortBy: !header.sorting,
        Cell: (row) => {
          if (row.column.id === "nm") {
            return (
              <span
                className="dealer-name"
                onClick={() => {
                  navigate(
                    `${PATHS.SHOW_DEALERS}/${PATHS.SPECIFIC_DEALER}?idx=${row.cell.row.original.idx}`,
                    {
                      state: {
                        dealerDetails: row.cell.row.original,
                        from: oemId && `${PATHS.SHOW_DEALERS}/${PATHS.OEM_DEALERS}?oemId=${oemId}`,
                      },
                    }
                  );
                }}
              >
                {row.value}
              </span>
            );
          }
          if (row.column.id === "stock" || row.column.id === "installed") {
            return <span className="cell-value">{row.value || 0}</span>;
          }
          return <span className="cell-value">{row.value === 0 ? "0" : row.value || "-"}</span>;
        },
      })),
    [navigate, oemId]
  );

  const oemTableColumns = useMemo(
    () =>
      Object.values(OEM_TABLE_HEADERS).map((header) => ({
        Header: header.nm,
        accessor: header.key,
        disableSortBy: !header.sorting,
        Cell: (row) => {
          if (row.column.id === "nod") {
            return (
              <span
                className="nod"
                onClick={() => {
                  if (row.cell.row.original.idx) {
                    navigate(`${PATHS.SHOW_DEALERS}/${PATHS.OEM_DEALERS}?oemId=${row.cell.row.original.idx}`, {
                      state: { oemDetails: row.cell.row.original },
                    });
                  }
                }}
              >
                {row.value}
              </span>
            );
          }
          if (row.column.id === "stock" || row.column.id === "installed") {
            return <span className="cell-value">{row.value || 0}</span>;
          }
          return <span className="cell-value">{row.value === 0 ? "0" : row.value || "-"}</span>;
        },
      })),
    [navigate]
  );

  const handleSwitcherChange = (tab) => {
    setSelected(tab.idx);
    if (tab.idx === 1) {
      setFilteredDealers(allDealers);
    }
  };

  const breadcrumbPaths = [
    { idx: 1, name: "OEMs", link: `${PATHS.SHOW_DEALERS}`, cb: () => setSelected(2) },
    { idx: 2, name: oemId && oemDetails && oemDetails.oemNm, link: ``, isCurrentPage: true },
  ];

  const switcherOptions = [
    { idx: 1, label: "All Dealerships" },
    { idx: 2, label: "OEMs" },
  ];

  return (
    <div className="dealers-table">
      {showLoader ? (
        <Spinner />
      ) : (
        <>
          <div className="add-btn-container">
            {oemId ? (
              <div className="breadcrumb-container">
                <Breadcrumb paths={breadcrumbPaths} />
              </div>
            ) : (
              <Switcher options={switcherOptions} className={`dealers-switcher`} selectedOption={selected} handleChange={handleSwitcherChange} />
            )}
            <Button className="add-new-btn" onClick={() => navigate(PATHS.ADD_DEALER)}>
              <AddNew /> Add New Dealer
            </Button>
          </div>

          <ReactTable
            columns={selected === 1 ? dealerTableColumns : oemTableColumns}
            data={selected === 1 ? filteredDealers : allOems}
            showSearch={true}
            pageSize={10}
          />
        </>
      )}
    </div>
  );
};

export default ShowDealers;
